/**
 * ChunkletItem.f.jsx (SBR-3532 / Challenger 3 chunklet component)
 * Renders a single related search term with a small product image beside it.
 * Handles placeholder images for missing/failed slots and click-to-search navigation.
 * AC-001: Chunklet visual (term + single image)
 * AC-012: Image display
 * AC-013: 0 images -> placeholder (decorative, alt="")
 * AC-016: Screen reader labels (aria-label)
 * AC-017: 4.5:1 contrast (black on white)
 */
import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text } from 'components/ui';
import { PLACEHOLDER_IMAGE_SRC } from 'components/RelatedSearches/constants';
import { space, colors, fontSizes } from 'style/config';
import localeUtils from 'utils/LanguageLocale';
import relatedSearchesEN from 'components/RelatedSearches/locales/relatedSearches_en_US';
import relatedSearchesFR from 'components/RelatedSearches/locales/relatedSearches_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(relatedSearchesEN, relatedSearchesFR);

/**
 * ChunkletItem: horizontal term + single image for chunklet variant.
 * Phase 1: Term label + image (1 image per term, not 2x2 grid)
 * Phase 2: Accessibility (aria-label, keyboard focusable, color contrast)
 * @param {Object} props
 * @param {string} props.term - Search term label
 * @param {Array} props.images - Array of image objects (uses first one)
 * @param {Function} props.onClick - Click handler (receives term), debounced by parent
 */
function ChunkletItem({ term, images, onClick }) {
    // P1-002: Use first image if available, otherwise use placeholder (AC-013)
    const hasImage = images && images.length > 0 && images[0]?.url;
    const imageSrc = hasImage ? images[0].url : PLACEHOLDER_IMAGE_SRC;
    // AC-013: decorative image -> empty alt text
    const imageAlt = '';

    // P1-003: Click handler delegates to parent's debounced handleTermClick (matching RecapCardItem pattern)
    const handleClick = () => {
        onClick(term);
    };

    // P2-001: Localized aria-label for screen readers
    const ariaLabel = getText('searchForTerm', [term]);

    return (
        <Box
            is='li'
            data-testid='chunklet-item'
        >
            <button
                type='button'
                css={styles.button}
                onClick={handleClick}
                aria-label={ariaLabel}
            >
                {/* P1-001: Single product image */}
                <Box css={styles.imageWrapper}>
                    <img
                        src={imageSrc}
                        alt={imageAlt}
                        css={styles.image}
                    />
                </Box>
                {/* P1-001: Term label */}
                <Text
                    data-testid='chunklet-term'
                    css={styles.termLabel}
                    children={term}
                />
            </button>
        </Box>
    );
}

// Co-located styles (UFE convention)
const styles = {
    button: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        padding: 0,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'center',
        color: colors.black, // AC-017: black text on white background (>=4.5:1 contrast)
        '&:focus': {
            outline: `2px solid ${colors.black}`, // P2-002: visible focus outline
            outlineOffset: `${space[1]}px` // P2-002: 4px offset
        }
    },
    imageWrapper: {
        width: `${space[12]}px`, // 64px (space[12] = 64 in design tokens)
        height: `${space[12]}px`,
        borderRadius: '50%',
        overflow: 'hidden',
        backgroundColor: colors.gray[100],
        flexShrink: 0
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    termLabel: {
        fontSize: `${fontSizes.sm}px`,
        lineHeight: 1.3,
        marginTop: `${space[1]}px`,
        maxWidth: `${space[16]}px`, // 80px (space[16] = 80)
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-word'
    }
};

ChunkletItem.propTypes = {
    term: PropTypes.string.isRequired,
    images: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string.isRequired,
            altText: PropTypes.string
        })
    ).isRequired,
    onClick: PropTypes.func.isRequired
};

export default wrapFunctionalComponent(ChunkletItem, 'ChunkletItem');
